/**
 * Krok UX1 — POVINNÉ BRÁNY, JEDEN VÝPOČET.
 *
 * Nález AUD1 28. 9. 2026: hlavička zakázky psala „Připravenost 0 ze 5
 * povinných bran", dlaždice vedle „Otevřené brány 0 z 0 povinných".
 * Čísla se brala ze dvou různých tvarů dat:
 *
 *  - `fixture.trust.decisionReadiness.mandatoryGatesTotal` = konstanta
 *    podle typu zakázky (LAND 4 / HOUSE 5 / FLAT 3),
 *  - `fixture.gates.length` = to, co vrátí `GET /detail`. V produkci je
 *    ten seznam PRÁZDNÝ: `persist.ts` plní `claimType` hodnotou
 *    `dd_facts.fact_key` a žádný reálný `fact_key` nesplňuje claim typ
 *    z registru bran, takže `evaluateGate` vrací `null` a bran není.
 *
 * ROZHODNUTÍ (krok UX1, `step-UX1` bod 3): `packages/contexts/duediligence`
 * je zlatý standard, výpočet se v tomto kroku NEMĚNÍ. Sjednocení tedy
 * uděláme v display vrstvě: jmenovatel je jeden (mandatory total, jinak
 * délka seznamu), uzavřené i otevřené se od něj počítají, a když
 * serverový seznam jmenovatele nepokrývá, UI to přizná místo toho, aby
 * tvrdilo „všechny brány zavřené". Vada samotná (chybějící mapování
 * `fact_key` → `claimType`) je NÁLEZ pro linku A, viz REPORT-step-UX1.
 *
 * Krok UX2 přidává druhý parametr — ZJIŠTĚNÝ typ předmětu. Zakázka
 * Poděbradova 141/44 má zadání „Pozemek" a data „Dům s pozemky"; server podle
 * zadání počítal 4 brány místo 5. Když se rozchází, jmenovatel se přepočítá
 * z `MANDATORY_GATES_BY_CASE_KIND` (krok UX2 ROZHODNUTÍ — server-side oprava
 * je NÁLEZ pro linku A, tady je to jen zobrazení).
 */

import type { CaseDetail } from '@reos/reports';
import type { R3CaseFixture } from '@reos/duediligence';
import { MANDATORY_GATES_BY_CASE_KIND } from './gate-catalog.js';
import type { GateCaseKind } from './case-subject.js';

type GateState = R3CaseFixture['gates'][number];

export interface GateProgress {
  /** Povinné brány celkem (jmenovatel obou čísel na obrazovce). */
  readonly total: number;
  readonly closed: number;
  readonly open: number;
  /** Podíl uzavřených v procentech; `null` = jmenovatel je 0. */
  readonly pct: number | null;
  /** Brány, které umíme pojmenovat (ze seznamu serveru) a nejsou zavřené. */
  readonly namedOpen: readonly GateState[];
  /** Seznam ze serveru pokrývá celý jmenovatel — jinak se nesmí psát „vše zavřené“. */
  readonly listCoversTotal: boolean;
  readonly allClosed: boolean;
}

/**
 * Krok UX2 — jmenovatel podle ZJIŠTĚNÉHO typu, ne podle zadání.
 *
 * `decisionReadiness` na serveru počítá z `dd_cases.kind` (to, co makléř
 * vybral v průvodci). Zakázka Poděbradova 141/44 má v hlavičce „Dům s pozemky",
 * ale zadání říká „Pozemek" — a server tak počítal 4 povinné brány místo 5.
 *
 *   - když zjištěný typ odpovídá zadání, je to tatáž konstanta, jakou použil
 *     server, a jeho `mandatoryGatesClosed` zustava autoritativni (i kdyz
 *     seznam bran z běhu je prázdný — NÁLEZ 1 kroku UX1),
 *   - když se rozchází, přepočítá se jmenovatel ze `MANDATORY_GATES_BY_CASE_KIND`
 *     a uzavřené se spočítá jen z bran, které se v seznamu opravdu jmenují
 *     (u domu chybí `building_legality` a `structural_safety`, takže ty se
 *     počítat nemají čím).
 */
export function gateProgress(detail: CaseDetail, detectedKind: GateCaseKind | null = null): GateProgress {
  const gates = detail.fixture.gates;
  const readiness = detail.fixture.trust.decisionReadiness;
  const detected = detectedKind === null ? null : MANDATORY_GATES_BY_CASE_KIND[detectedKind];
  const sameAsDeclared =
    detected === null ||
    detail.fixture.trust.caseKind === '' ||
    detail.fixture.trust.caseKind === detectedKind;

  // Jmenovatel: konstanta podle typu zakázky, jinak (když ji API neposlalo)
  // délka toho, co server vrátil. Nikdy dvě různé čísla na jedné obrazovce.
  const total =
    detected !== null
      ? detected.length
      : readiness.mandatoryGatesTotal > 0
        ? readiness.mandatoryGatesTotal
        : gates.length;
  const closed = Math.min(
    Math.max(sameAsDeclared ? readiness.mandatoryGatesClosed : closedByClaimType(detected, gates), 0),
    total,
  );
  const open = Math.max(total - closed, 0);
  const namedOpen = gates.filter((gate) => gate.state !== 'CLOSED');
  return {
    total,
    closed,
    open,
    pct: total > 0 ? Math.round((closed / total) * 100) : null,
    namedOpen,
    listCoversTotal: namedOpen.length === open,
    allClosed: total > 0 && closed === total,
  };
}

/** Uzavřené z POJMENOVANÝCH bran — jen ty, co jsou v seznamu ze serveru. */
function closedByClaimType(
  mandatory: readonly string[] | undefined,
  gates: readonly GateState[],
): number {
  if (mandatory === undefined) return 0;
  const closedTypes = new Set(
    gates.filter((gate) => gate.state === 'CLOSED').map((gate) => gate.claimType),
  );
  return mandatory.filter((claimType) => closedTypes.has(claimType)).length;
}
